import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth-guard.guard';
import { MsalGuard } from './core/guards/msal.guard';
import { TenantGuard } from './core/guards/tenant.guard';
import { CONTENT_PAGE_DIALOG_SERVICE_TOKEN, REDEEM_PAGE_DIALOG_SERVICE_TOKEN } from './page-edit-screen/page-edit-screen.tokens';
import { PageType } from './page-edit-screen/page-type';
import { TenantIndependentGuard } from './tenant-independent/guards/tenant-independent.guard';

const routes: Routes = [
  {
    path: 'select-tenant',
    loadChildren: () => import('./tenant-select-screen/tenant-select-screen.module').then((m) => m.TenantSelectScreenModule),
    canLoad: [MsalGuard],
    pathMatch: 'full',
    canActivate: [TenantIndependentGuard],
  },
  {
    path: 'root',
    loadChildren: () => import('./tenant-independent/tenant-independent.module').then((m) => m.TenantIndependentModule),
    canLoad: [MsalGuard],
  },
  {
    path: 'pages/content/:pageVersionId',
    loadChildren: () => import('./page-edit-screen/page-edit-screen.module').then((m) => m.PageEditScreenModule),
    data: { pageType: PageType.content, dialogServiceToken: CONTENT_PAGE_DIALOG_SERVICE_TOKEN },
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  /*
    TODO
    reenable when reintroducing collection pages
  {
    path: 'pages/collection/:pageVersionId',
    loadChildren: () => import('./page-edit-screen/page-edit-screen.module').then((m) => m.PageEditScreenModule),
    data: { pageType: PageType.collection, dialogServiceToken: CONTENT_PAGE_DIALOG_SERVICE_TOKEN },
    canLoad: [MsalGuard],
  },
  */
  {
    path: 'pages/genre/:pageVersionId',
    loadChildren: () => import('./page-edit-screen/page-edit-screen.module').then((m) => m.PageEditScreenModule),
    data: { pageType: PageType.genre, dialogServiceToken: CONTENT_PAGE_DIALOG_SERVICE_TOKEN },
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'pages/marketing/details-page/:pageVersionId',
    loadChildren: () => import('./page-edit-screen/page-edit-screen.module').then((m) => m.PageEditScreenModule),
    data: { pageType: PageType.marketingDetails, dialogServiceToken: CONTENT_PAGE_DIALOG_SERVICE_TOKEN },
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'pages/marketing/landing-page/:pageVersionId',
    loadChildren: () => import('./page-edit-screen/page-edit-screen.module').then((m) => m.PageEditScreenModule),
    data: { pageType: PageType.landing, dialogServiceToken: CONTENT_PAGE_DIALOG_SERVICE_TOKEN },
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'pages/marketing/app-landing-mobile/:pageVersionId',
    loadChildren: () => import('./page-edit-screen/page-edit-screen.module').then((m) => m.PageEditScreenModule),
    data: { pageType: PageType.appLandingMobile, dialogServiceToken: CONTENT_PAGE_DIALOG_SERVICE_TOKEN },
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'pages/marketing/app-landing-tablet/:pageVersionId',
    loadChildren: () => import('./page-edit-screen/page-edit-screen.module').then((m) => m.PageEditScreenModule),
    data: { pageType: PageType.appLandingTablet, dialogServiceToken: CONTENT_PAGE_DIALOG_SERVICE_TOKEN },
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'pages/marketing/app-landing-cotv/:pageVersionId',
    loadChildren: () => import('./page-edit-screen/page-edit-screen.module').then((m) => m.PageEditScreenModule),
    data: { pageType: PageType.appLandingCotv, dialogServiceToken: CONTENT_PAGE_DIALOG_SERVICE_TOKEN },
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'pages/marketing/app-landing-apple-android-tv/:pageVersionId',
    loadChildren: () => import('./page-edit-screen/page-edit-screen.module').then((m) => m.PageEditScreenModule),
    data: { pageType: PageType.appLandingAppleAndroidTv, dialogServiceToken: CONTENT_PAGE_DIALOG_SERVICE_TOKEN },
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'pages/redeem/:pageVersionId',
    loadChildren: () => import('./page-edit-screen/page-edit-screen.module').then((m) => m.PageEditScreenModule),
    data: { pageType: PageType.redeem, dialogServiceToken: REDEEM_PAGE_DIALOG_SERVICE_TOKEN },
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'pages',
    loadChildren: () => import('./page-screen/page-screen.module').then((m) => m.PageScreenModule),
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },

  {
    path: 'content-groups',
    loadChildren: () => import('./content-group-list-screen/content-group-list-screen.module').then((m) => m.ContentGroupListScreenModule),
    canLoad: [MsalGuard],
    pathMatch: 'full',
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'content-groups/:contentGroupId',
    loadChildren: () => import('./content-group-edit-screen/content-group-edit-screen.module').then((m) => m.ContentGroupEditScreenModule),
    canLoad: [MsalGuard],
    pathMatch: 'full',
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'menu-editor',
    loadChildren: () => import('./menu-editor/menu-editor.module').then((m) => m.MenuEditorModule),
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'application-list-and-metadata',
    loadChildren: () =>
      import('./application-list-and-metadata/application-list-and-metadata.module').then((m) => m.ApplicationListAndMetadataModule),
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'brand-settings',
    loadChildren: () => import('./brand-settings/brand-settings.module').then((m) => m.BrandSettingsModule),
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'channel-list',
    loadChildren: () => import('./channel-list/channel-list.module').then((m) => m.ChannelListModule),
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'config-editor',
    loadChildren: () => import('./config-editor-screen/config-editor-screen.module').then((m) => m.ConfigEditorScreenModule),
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'redirect-policies',
    loadChildren: () => import('./redirect-policies/redirect-policies.module').then((m) => m.RedirectPoliciesModule),
    canLoad: [MsalGuard],
    canActivate: [TenantGuard, AuthGuard],
  },
  {
    path: 'no-permission',
    loadChildren: () => import('./no-permission/no-permission.module').then((m) => m.NoPermissionModule),
    canLoad: [MsalGuard],
  },
  // { path: 'logout', component: LogoutComponent },
  { path: '', pathMatch: 'full', redirectTo: 'pages' },
  { path: '**', redirectTo: 'pages' },
];

const isIframe = window !== window.parent && !window.opener;

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      paramsInheritanceStrategy: 'always',
      // Don't perform initial navigation in iframes
      initialNavigation: !isIframe ? 'enabledNonBlocking' : 'disabled',
      relativeLinkResolution: 'legacy',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
